import { WarehouseElementLocation } from "components/WarehouseElements/WarehouseElements";
import { TestIds } from "constants/Tests/TestIds";
import { useElementDetails } from "hooks/useElementDetails";
import React, { JSX, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePostActionRequestMutation } from "services/api/warehouseApi";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import DetailsAction from "./DetailsAction/DetailsAction";
import {
  ElementDetailsAction,
  ElementDetailsValue,
} from "models/Resources/ElementDetailsResource";
import "./DetailsWindow.scss";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createErrorComponent } from "components/Errors/ErrorComponent";
import { INTERACTIVE_CLASSNAME_SUFFIX } from "../InteractiveGridElement/InteractiveGridElement";
import { WAREHOUSE_GRID_ID } from "../WarehouseGrid/WarehouseGrid";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import { selectStoreNb } from "store/Selectors/locationsSelector";

type Detail = {
  label: JSX.Element;
  value: JSX.Element;
};

type WindowInfo = {
  details: Detail[];
  actions: JSX.Element[];
};

type Props = {
  id: string;
  location?: WarehouseElementLocation;
};

export const DETAIL_WINDOW_ID = "detail-window";
const MAX_DETAIL_WINDOW_WIDTH = 200;

export function DetailsWindow({ id }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const storeNb = useAppSelector(selectStoreNb);
  const [isWindowLeftOfElement, setWindowLeftOfElement] = useState(false);
  const [windowInfos, setWindowInfos] = useState<WindowInfo>();

  const [
    sendActionRequest,
    {
      isSuccess: isPostSuccess,
      data: postResponse,
      isLoading: isPostLoading,
      error: postError,
    },
  ] = usePostActionRequestMutation();
  let isLastPostResponseObsolete = false;

  useEffect(() => {
    const selectedElementNode = document.getElementsByClassName(
      `${id}${INTERACTIVE_CLASSNAME_SUFFIX}`
    )[0];
    const warehouseGridSize = document
      .getElementById(`${WAREHOUSE_GRID_ID}`)
      ?.getBoundingClientRect();

    if (selectedElementNode && warehouseGridSize) {
      const rect = selectedElementNode.getBoundingClientRect();
      setWindowLeftOfElement(
        rect.right + MAX_DETAIL_WINDOW_WIDTH > warehouseGridSize.right
      );
    }
  }, [id]);

  useEffect(() => {
    if (isPostLoading && isLastPostResponseObsolete) {
      isLastPostResponseObsolete = false;
    } else if (isPostSuccess && !isLastPostResponseObsolete && postResponse) {
      const responseDetails: Detail[] = [];
      const responseActions: JSX.Element[] = [];
      for (const detail of postResponse.details) {
        responseDetails.push(createDetail(detail));
      }

      for (const action of postResponse.actions) {
        responseActions.push(createActionButton(action));
      }
      setWindowInfos({ actions: responseActions, details: responseDetails });
    }
  }, [isPostSuccess, isPostLoading]);

  const {
    detailsInfos,
    error: detailError,
    isLoading: isElementDetailsLoading,
  } = useElementDetails(storeNb, id);

  const token = useRef(useAppSelector(state => state.appState.token));

  if (isElementDetailsLoading) {
    isLastPostResponseObsolete = true;
    return <></>;
  }

  const handleWindowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  //GT: Ajouter onFulfilled dans le then.
  const handleActionClick = async (action: string) => {
    try {
      await sendActionRequest({
        storeNb: storeNb,
        elementId: id,
        action: action,
        token: token.current,
      }).unwrap();
      dispatch(setOpenedDetailId(null));
    } catch (err) {
      console.error("Erreur lors de l'éxecution de l'action", err);
    }
  };

  function createDetail(detail: ElementDetailsValue): Detail {
    const label = (
      <td
        className={`details-labels details-${detail.etiquette}`}
        key={"label-" + detail.etiquette + "-" + detail.valeur}>
        {detail.etiquette + ":"}
      </td>
    );

    const value = (
      <td
        className={`details-value details-${detail.valeur}`}
        key={"value-" + detail.etiquette + "-" + detail.valeur}>
        {detail.valeur}
      </td>
    );

    return { label, value };
  }

  function getActionButtons() {
    if (windowInfos && !isLastPostResponseObsolete) return windowInfos.actions;
    const detailActions: JSX.Element[] = [];
    detailsInfos?.actions.map(action =>
      detailActions.push(createActionButton(action))
    );
    return detailActions;
  }

  function createActionButton(action: ElementDetailsAction): JSX.Element {
    return (
      <td key={"td" + action.etiquette} className="detail-action-button">
        <DetailsAction
          key={action.etiquette}
          elementAction={action}
          onActionClick={handleActionClick}
        />
      </td>
    );
  }

  function getTableBody() {
    const body = [];
    if (windowInfos && !isLastPostResponseObsolete) {
      for (let i = 0; i < windowInfos.details.length; i++) {
        body.push(
          <tr className="details-body-row" key={i}>
            {windowInfos.details[i].label}
            {windowInfos.details[i].value}
          </tr>
        );
      }
    } else {
      detailsInfos?.details.map(detail => {
        const detailToRender = createDetail(detail);
        body.push(
          <tr
            className="details-body-row"
            key={detail.etiquette + detail.valeur}>
            {detailToRender.label}
            {detailToRender.value}
          </tr>
        );
      });
    }
    return body;
  }
  if (detailError || postError) {
    if (postError && (postError as FetchBaseQueryError).data) {
      return createErrorComponent(postError as FetchBaseQueryError);
    } else {
      return createErrorComponent(detailError as FetchBaseQueryError);
    }
  }

  return createPortal(
    <div
      id={DETAIL_WINDOW_ID}
      data-testid={TestIds.DETAILS_WINDOW}
      className="details-window"
      tabIndex={-1}
      onClick={handleWindowClick}
      style={{
        ...(isWindowLeftOfElement
          ? { right: "calc(100% + 5px)" }
          : { left: "calc(100% + 5px)" }),
      }}>
      <table>
        <tbody className="details-body">{getTableBody()}</tbody>
        <tfoot>
          <tr className="actions-details-buttons">{getActionButtons()}</tr>
        </tfoot>
      </table>
    </div>,
    document.getElementById("etiquette" + id)
      ? document.getElementById("etiquette" + id)!
      : document.body
  );
}
